import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import type { ViewLayoutRoleEntity } from './view-layout-role.entity';

export type ViewLayoutVisibility = 'organization' | 'role';

/**
 * Display style for special field rendering
 * - 'default': Standard text display
 * - 'phone': ClickToDialPhone component
 * - 'email': Mailto link
 * - 'url': External link
 * - 'badge': Badge component with variant
 * - 'currency': Formatted currency
 * - 'date': Formatted date
 * - 'datetime': Formatted datetime
 * - 'boolean': Yes/No display
 * - 'user': User name display (firstName lastName)
 */
export type FieldDisplayStyle =
	| 'default'
	| 'phone'
	| 'email'
	| 'url'
	| 'badge'
	| 'currency'
	| 'date'
	| 'datetime'
	| 'boolean'
	| 'user';

export interface ViewFieldPlacement {
	fieldId: string;
	/** How many of 12 grid columns this field spans. Valid snap values: 3, 4, 6, 8, 9, 12. Defaults to 6 (half width). */
	colSpan?: number;
	label?: string;
	displayStyle?: FieldDisplayStyle;
}

/**
 * Section type determines how the section is rendered
 * - 'fields': Standard field-based section with configurable columns
 * - 'component': Renders a registered component by componentId
 */
export type ViewSectionType = 'fields' | 'component';

/**
 * Condition for showing/hiding a section
 * - field: The field to check
 * - operator: Comparison operator
 * - value: Value to compare against (optional for exists/notExists)
 */
export interface ViewSectionCondition {
	field: string;
	operator: 'equals' | 'notEquals' | 'exists' | 'notExists' | 'in' | 'notIn';
	value?: unknown;
}

export interface ViewSection {
	id: string;
	title: string;
	sectionType: ViewSectionType;
	collapsible?: boolean;
	defaultCollapsed?: boolean;
	showCondition?: ViewSectionCondition;
	// For 'fields' type sections
	fields?: Array<ViewFieldPlacement>;
	// For 'component' type sections
	componentId?: string;
	componentProps?: Record<string, unknown>;
	/** How many of 12 grid columns this section spans. Valid snap values: 3, 4, 6, 8, 9, 12. Defaults to 12 (full width). */
	colSpan?: number;
	/** How many grid rows this section spans. When omitted, auto-calculated: 1 (header) + field rows for field sections, or defaultRowSpan from ComponentRegistry for component sections. */
	rowSpan?: number;
	/** Force this section to start at a specific grid column (1-based). When set, the placement algorithm will only place the section at this column, scanning downward for the first available row. */
	gridColumnStart?: number;
}

export interface ViewLayoutConfiguration {
	sections: Array<ViewSection>;
	globalSettings?: {
		showEmptyFields?: boolean;
		showActivityTimeline?: boolean;
		/**
		 * 'compact' (PD-14351) renders a denser detail: no per-section headers,
		 * tighter padding, and a tighter field grid. Defaults to the roomy
		 * 'default' layout when unset.
		 */
		density?: 'default' | 'compact';
	};
}

@Index('idx_view_layout_entity_view', ['entityType', 'viewType'], {})
@Index('idx_view_layout_visibility', ['visibility'], {})
@Index('idx_view_layout_sort', [
	'entityType',
	'viewType',
	'sortOrder',
	'deletedDate',
])
@Entity('view_layout_configuration')
export class ViewLayoutConfigurationEntity extends BaseEntity {
	@Column('varchar', { name: 'entityType', length: 50 })
	public entityType!: string;

	@Column('varchar', { name: 'viewType', length: 50, default: 'detail' })
	public viewType!: string;

	@Column('varchar', { name: 'name', length: 128 })
	public name!: string;

	@Column('enum', {
		name: 'visibility',
		enum: ['organization', 'role'],
		default: 'organization',
	})
	public visibility!: ViewLayoutVisibility;

	@Column('int', {
		name: 'sortOrder',
		nullable: true,
		unsigned: true,
		default: () => "'0'",
	})
	public sortOrder!: number;

	@Column('json', { name: 'layout' })
	public layout!: ViewLayoutConfiguration;

	@OneToMany(
		'ViewLayoutRoleEntity',
		(role: ViewLayoutRoleEntity) => role.fkViewLayout,
	)
	public sharedRoles!: Array<ViewLayoutRoleEntity>;
}
